<template>
  <div 
    class="markdown-content" 
    v-html="renderedContent"
    :class="{ 'markdown-user': isUser, 'markdown-bot': !isUser }"
  />
</template>

<script>
import MarkdownIt from 'markdown-it'

export default {
  name: 'MarkdownRenderer',
  props: {
    source: {
      type: [String, Object, Array, Number, Boolean],
      default: '',
      required: false
    },
    anchorAttributes: {
      type: Object,
      default: () => ({
        target: '_blank',
        rel: 'noopener noreferrer nofollow'
      })
    },
    isUser: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: () => ({
        html: false,         // Enable HTML tags in source
        xhtmlOut: false,     // Use '/' to close single tags (<br />)
        breaks: true,        // Convert '\n' in paragraphs into <br>
        langPrefix: 'language-',  // CSS language prefix for fenced blocks
        linkify: true,       // Autoconvert URL-like text to links
        typographer: true,   // Enable some language-neutral replacement + quotes beautification
        quotes: '""\'\'',       // Double + single quotes replacement pairs
      })
    }
  },
  data() {
    return {
      md: null
    }
  },
  computed: {
    // Ensure content is always a string
    normalizedContent() {
      try {
        // ตรวจสอบและแปลง source ให้เป็น string
        if (typeof this.source === 'string') {
          return this.source.trim()
        }
        
        if (this.source === null || this.source === undefined) {
          return ''
        }
        
        // ถ้าเป็น object หรือ array ให้แปลงเป็น JSON string
        if (typeof this.source === 'object') {
          return JSON.stringify(this.source, null, 2)
        }
        
        // แปลงประเภทอื่นๆ เป็น string
        return String(this.source)
      } catch (error) {
        console.error('Error normalizing content:', error)
        return 'Invalid content format'
      }
    },
    
    // Render markdown to HTML
    renderedContent() {
      try {
        if (!this.normalizedContent) {
          return ''
        }
        
        const html = this.md.render(this.normalizedContent)
        return this.sanitizeHtml(html)
      } catch (error) {
        console.error('Error rendering markdown:', error)
        return `<p style="color: #ef5350;">Error rendering content: ${error.message}</p>`
      }
    }
  },
  created() {
    this.setupMarkdownIt()
  },
  methods: {
    async setupMarkdownIt() {
      try {
        // Initialize markdown-it with options
        this.md = new MarkdownIt(this.options)
        
        // Load plugins dynamically to avoid import errors
        await this.loadPlugins()
        
        // Configure plugins and rules
        this.configureRenderer()
      } catch (error) {
        console.error('Error setting up markdown-it:', error)
        // Fallback to basic markdown-it
        this.md = new MarkdownIt(this.options)
        this.configureRenderer()
      }
    },
    
    async loadPlugins() {
      try {
        // Load emoji plugin
        const emojiModule = await import('markdown-it-emoji')
        const emoji = emojiModule.default || emojiModule
        this.md.use(emoji)
      } catch (error) {
        console.warn('Could not load markdown-it-emoji:', error)
      }
      
      try {
        // Load anchor plugin
        const anchorModule = await import('markdown-it-anchor')
        const anchor = anchorModule.default || anchorModule
        this.md.use(anchor)
      } catch (error) {
        console.warn('Could not load markdown-it-anchor:', error)
      }
    },
    configureRenderer() {
      const originalLinkRender = this.md.renderer.rules.link_open || function(tokens, idx, options, env, renderer) {
        return renderer.renderToken(tokens, idx, options)
      }
      
      // Custom link renderer to add attributes
      this.md.renderer.rules.link_open = (tokens, idx, options, env, renderer) => {
        const token = tokens[idx]
        
        // Add custom attributes
        Object.entries(this.anchorAttributes).forEach(([key, value]) => {
          token.attrSet(key, value)
        })
        
        return originalLinkRender(tokens, idx, options, env, renderer)
      }
      
      // Custom image renderer for better handling
      this.md.renderer.rules.image = (tokens, idx, options, env, renderer) => {
        const token = tokens[idx]
        const src = token.attrGet('src')
        const alt = token.content
        const title = token.attrGet('title') || ''
        
        return `<img src="${src}" alt="${alt}" title="${title}" style="max-width: 100%; height: auto; border-radius: 8px;" loading="lazy" />`
      }
      
      // Custom code block renderer
      const originalCodeBlock = this.md.renderer.rules.code_block || function(tokens, idx, options, env, renderer) {
        return renderer.renderToken(tokens, idx, options)
      }
      
      this.md.renderer.rules.code_block = (tokens, idx, options, env, renderer) => {
        const token = tokens[idx]
        const content = token.content
        
        return `<pre class="code-block"><code>${this.escapeHtml(content)}</code></pre>`
      }
    },
    
    configurePlugins() {
      // You can add markdown-it plugins here
      // Example: this.md.use(require('markdown-it-emoji'))
    },
    
    sanitizeHtml(html) {
      // Basic HTML sanitization (you can use DOMPurify for more robust sanitization)
      return html
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '')
    },
    
    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }
      return text.replace(/[&<>"']/g, (m) => map[m])
    }
  },
  watch: {
    source: {
      immediate: true,
      handler(newValue) {
        // Log for debugging in development
        if (process.env.NODE_ENV === 'development') {
          // console.log('MarkdownRenderer source changed:', {
          //   type: typeof newValue,
          //   value: newValue
          // })
        }
      }
    },
    options: {
      deep: true,
      handler() {
        this.setupMarkdownIt()
      }
    }
  }
}
</script>

<style scoped>
.markdown-content {
  word-wrap: break-word;
  line-height: 1.6;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* User message styling */
.markdown-user {
  color: #333;
}

/* Bot message styling */
.markdown-bot {
  color: inherit;
}

/* Typography */
.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-weight: 600;
  line-height: 1.25;
}

.markdown-content :deep(h1) { font-size: 1.5em; }
.markdown-content :deep(h2) { font-size: 1.3em; }
.markdown-content :deep(h3) { font-size: 1.1em; }
.markdown-content :deep(h4) { font-size: 1em; }
.markdown-content :deep(h5) { font-size: 0.9em; }
.markdown-content :deep(h6) { font-size: 0.8em; }

.markdown-content :deep(p) {
  margin: 0;
  margin-bottom: 1em;
  display: block;
}

.markdown-content :deep(br) {
  content: "";
  margin: 0;
  display: block;
  font-size: 24%;
}

/* Lists */
.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin-bottom: 1em;
  padding-left: 2em;
}

.markdown-content :deep(li) {
  margin-bottom: 0.25em;
  margin-top: -1em;
}

.markdown-content :deep(li p) {
  margin-top: -22px;
  margin-bottom: 0.5em;
}

.markdown-content :deep(li p strong) {
  margin-top: -22px;
  display: block;
}

.markdown-content :deep(ul) {
  margin-bottom: -20px;
}

.markdown-content :deep(ol) {
  margin-bottom: -25px;
}

.markdown-content :deep(ol li ul) {
  margin-bottom: -40px;
}

.markdown-content :deep(ul.task-list) {
  list-style: none;
  padding-left: 1em;
}

.markdown-content :deep(ul.task-list li) {
  margin-top: -20px;
}

/* Blockquotes */
.markdown-content :deep(blockquote) {
  border-left: 4px solid #ddd;
  padding-left: 1em;
  margin: 1em 0;
  margin-top: -35px;
  color: #666;
  font-style: italic;
}

/* Code */
.markdown-content :deep(code) {
  background-color: transparent;
  color: #2196F3;
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace;
  font-size: 0.85em;
  white-space: pre-wrap;
}

.markdown-content :deep(pre) {
  background: #2b2b2b;
  color: #f0f8ff;
  padding: 1em;
  border-radius: 8px;
  overflow-x: auto;
  margin: 10px 0;
  border: 6px double white;
}

.markdown-content :deep(pre code) {
  background: #2b2b2b;
  color: #f0f8ff;
  display: block;
  padding: 0;
  border: none;
}

.markdown-content :deep(.code-block) {
  background: #2b2b2b;
  color: #f0f8ff;
  padding: 1em;
  border-radius: 8px;
  overflow-x: auto;
  margin: 10px 0;
}

/* Tables */
.markdown-content :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
  box-sizing: border-box;
  border-spacing: 2px;
  border-color: gray;
  display: table;
  text-indent: 0;
  color: #2b2b2b !important;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
  border: 1px solid #dfe2e5;
  padding: 6px 13px;
  text-align: left;
}

.markdown-content :deep(th) {
  background-color: #f5f5f5;
  font-weight: 600;
}

.markdown-content :deep(tr) {
  border-top: 1px solid #c6cbd1;
  background: #fff;
}

.markdown-content :deep(tr:nth-child(2n)) {
  background: #d8d9da;
}

/* Images */
.markdown-content :deep(img) {
  max-width: 100%;
  max-height: 320px;
  height: auto;
  border-radius: 8px;
  display: block;
  margin: 0.5em 0;
}

/* Links */
.markdown-content :deep(a) {
  color: #1976d2;
  text-decoration: none;
}

.markdown-content :deep(a:hover) {
  text-decoration: underline;
}

/* Horizontal rule */
.markdown-content :deep(hr) {
  border: none;
  border-top: 1px solid #ddd;
  margin: 2em 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .markdown-content {
    font-size: 0.9em;
  }
  
  .markdown-content :deep(table) {
    font-size: 0.8em;
  }
  
  .markdown-content :deep(pre) {
    padding: 0.5em;
    font-size: 0.8em;
  }
}
</style>